import { useEffect, useRef, type KeyboardEvent } from 'react';
import { PURCHASES, PurchaseKind, PurchaseState, getPurchaseState, purchaseCost, type PurchaseDefinition } from '../../game/data/purchases';
import type { GameRuntime } from '../../game/runtime/GameRuntime';
import type { RuntimeUISnapshot } from '../../game/runtime/RuntimeUIStore';
import { CORE_BEAMS, DEVICES, NODE_LENSES } from '../../game/weapons/definitions';

/**
 * Full-surface shop overlay for the R2 route. The frozen `?r2=0` path keeps
 * drawing its in-canvas menu (with GatePanel as the accessible mirror); this
 * component IS the gate UI where that canvas menu does not exist.
 *
 * Layout is column-major to match the sim's pad navigation: Up/Down step ±1
 * inside a column, Left/Right jump ±4 across columns (GameWorld
 * updateCalibrationGate uses the same arithmetic).
 */

const COLUMN_LABELS = ['CORE BEAM', 'NODE LENS', 'DEVICE', 'SYSTEMS'] as const;
const COLUMN_SIZE = 4;

const KEY_DELTAS: Record<string, number> = {
  ArrowUp: -1,
  ArrowDown: 1,
  ArrowLeft: -COLUMN_SIZE,
  ArrowRight: COLUMN_SIZE,
};

/**
 * authoring/gate-art/out/icons と同順（PURCHASES id 順）。実体は
 * public/assets/ui/gate-icons/<slug>.png（slice_icons.py 生成・手編集禁止）。
 */
const ICON_SLUGS = [
  'pulse', 'arc', 'prism', 'lattice',
  'focus', 'fan', 'mirror', 'tether',
  'repair', 'phase-cut', 'time-fold', 'overdrive',
  'hull-capacity', 'node-recalibration', 'score-phase', 'hull-repair',
] as const;

function iconSrc(item: PurchaseDefinition): string {
  return `/assets/ui/gate-icons/${ICON_SLUGS[item.id] ?? 'pulse'}.png`;
}

/** Equipment descriptions live on the weapon definitions; systems are shop-only. */
const SYSTEM_DESCRIPTIONS: Partial<Record<PurchaseKind, string>> = {
  [PurchaseKind.MaxLife]: 'Adds one permanent hull slot, filled on purchase.',
  [PurchaseKind.NodeRecharge]: 'Nodes recharge faster. Stacks per purchase.',
  [PurchaseKind.ScoreBoost]: 'Doubles all score for 45 seconds.',
  [PurchaseKind.Repair]: 'Restores one hull point.',
};

function purchaseDescription(item: PurchaseDefinition): string {
  if (item.kind === PurchaseKind.Core) return CORE_BEAMS[item.target]?.description ?? '';
  if (item.kind === PurchaseKind.Lens) return NODE_LENSES[item.target]?.description ?? '';
  if (item.kind === PurchaseKind.Device) return DEVICES[item.target]?.description ?? '';
  return SYSTEM_DESCRIPTIONS[item.kind] ?? '';
}

/** Column headers already carry the category, so cards drop the prefix. */
function shortName(item: PurchaseDefinition): string {
  const separator = item.name.indexOf(' / ');
  return separator < 0 ? item.name : item.name.slice(separator + 3);
}

function stateTag(state: PurchaseState, price: number): string {
  if (state === PurchaseState.Equipped) return 'EQUIPPED';
  if (state === PurchaseState.Equip) return 'EQUIP';
  if (state === PurchaseState.Maxed) return 'MAX';
  if (state === PurchaseState.RepairNotNeeded) return 'FULL';
  return `${price}`;
}

function actionLabel(state: PurchaseState, price: number): string {
  if (state === PurchaseState.Equip) return 'EQUIP';
  if (state === PurchaseState.Equipped) return 'EQUIPPED';
  if (state === PurchaseState.Maxed) return 'MAXED';
  if (state === PurchaseState.RepairNotNeeded) return 'FULL';
  if (state === PurchaseState.InsufficientLux) return `NEED ${price} LUX`;
  return `BUY · ${price} LUX`;
}

interface GateShopProps {
  runtime: GameRuntime;
  snapshot: RuntimeUISnapshot;
}

export function GateShop({ runtime, snapshot }: GateShopProps) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selected = PURCHASES[snapshot.gateSelection] ?? PURCHASES[0]!;
  const selectedState = getPurchaseState(runtime.world.player, selected, runtime.world.difficulty);
  const selectedPrice = purchaseCost(runtime.world.player, selected, runtime.world.difficulty);
  const selectedCount = runtime.world.player.purchaseCounts[selected.id] ?? 0;
  const canAct = selectedState === PurchaseState.Buy || selectedState === PurchaseState.Equip;
  const hint = snapshot.lastInputKind === 'touch'
    ? 'TAP AGAIN TO BUY · LEAVE GATE TO RESUME'
    : '↑↓←→ SELECT · ENTER BUY · ESC RESUME';

  // Keyboard keeps working with DOM focus parked on a card: arrows bubble up
  // here and drive the sim-owned selection, the effect below chases it.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    const delta = KEY_DELTAS[event.code];
    if (delta === undefined) return;
    event.preventDefault();
    runtime.selectPurchase((snapshot.gateSelection + delta + PURCHASES.length) % PURCHASES.length);
  };

  useEffect(() => {
    // preventScroll: a plain focus() lets the browser scroll the (overflow:
    // hidden) surface to reveal the card, dragging the whole game off-screen
    // on phones — the same failure the .game-canvas !important comment records.
    itemRefs.current[snapshot.gateSelection]?.focus({ preventScroll: true });
  }, [snapshot.gateSelection]);

  return (
    <section className="gate-shop" role="dialog" aria-modal="true" aria-labelledby="gate-shop-title" onKeyDown={onKeyDown}>
      <div className="gate-shop-panel">
        <header className="gate-shop-header">
          <div>
            <p className="eyebrow">STAGE SUSPENDED · SUPPLY CACHE</p>
            <h2 id="gate-shop-title">CALIBRATION GATE</h2>
            <p className="gate-shop-sub">SPEND LUX ON UPGRADES — LEAVE GATE TO RESUME THE STAGE</p>
          </div>
          <div className="gate-shop-lux">
            <span>LUX</span>
            <strong>{String(snapshot.lux).padStart(4, '0')}</strong>
          </div>
        </header>

        <div className="gate-shop-grid">
          {COLUMN_LABELS.map((label, column) => (
            <div className="gate-shop-column" key={label}>
              <h3>{label}</h3>
              {PURCHASES.slice(column * COLUMN_SIZE, (column + 1) * COLUMN_SIZE).map((item) => {
                const state = getPurchaseState(runtime.world.player, item, runtime.world.difficulty);
                const price = purchaseCost(runtime.world.player, item, runtime.world.difficulty);
                const count = runtime.world.player.purchaseCounts[item.id] ?? 0;
                const isSelected = item.id === snapshot.gateSelection;
                const className = [
                  'gate-item',
                  isSelected ? 'is-selected' : '',
                  state === PurchaseState.Equipped ? 'is-equipped' : '',
                  state === PurchaseState.InsufficientLux ? 'is-locked' : '',
                  state === PurchaseState.Maxed || state === PurchaseState.RepairNotNeeded ? 'is-spent' : '',
                ].filter(Boolean).join(' ');
                return (
                  <button
                    key={item.id}
                    ref={(el: HTMLButtonElement | null) => { itemRefs.current[item.id] = el; }}
                    data-menu-item
                    type="button"
                    className={className}
                    aria-pressed={isSelected}
                    onClick={() => (isSelected ? runtime.purchase(item.id) : runtime.selectPurchase(item.id))}
                  >
                    <img className="gate-item-icon" src={iconSrc(item)} alt="" width={24} height={24} />
                    <span className="gate-item-name">{shortName(item)}</span>
                    <span className="gate-item-tag">{stateTag(state, price)}</span>
                    {item.maxPurchases > 1 && (
                      <span className="gate-item-pips" aria-label={`${count} of ${item.maxPurchases}`}>
                        {'◆'.repeat(count)}{'◇'.repeat(item.maxPurchases - count)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <footer className="gate-shop-footer">
          <div className="gate-shop-detail">
            <p className="gate-detail-name">
              <img className="gate-detail-icon" src={iconSrc(selected)} alt="" width={24} height={24} />
              {selected.name}
              <span> {selectedCount}/{selected.maxPurchases}</span>
            </p>
            <p className="gate-detail-desc">{purchaseDescription(selected)}</p>
            <p className={`gate-detail-status${snapshot.gateMessage ? ' has-message' : ''}`} aria-live="polite">
              {snapshot.gateMessage || hint}
            </p>
          </div>
          <div className="gate-shop-actions">
            <button data-menu-item type="button" className="primary" disabled={!canAct} onClick={() => runtime.purchase(selected.id)}>
              {actionLabel(selectedState, selectedPrice)}
            </button>
            <button data-menu-item type="button" className="gate-leave" onClick={() => runtime.leaveGate()}>
              LEAVE GATE ▸ RESUME
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
}
